/**
 * The single door to core-api (ADR-005). Every request in the console goes through `api`.
 *
 * Session handling, and why it is shaped this way:
 *
 * - The **access token lives in a module variable**, never in storage. Short-lived and
 *   unreachable from another tab or from anything that reads `localStorage`.
 * - The **refresh token is an httpOnly cookie** we never see. `credentials: "include"` is what
 *   sends it, and it is only ever sent to `/auth/*` because the server scopes the cookie path.
 * - The **CSRF token is in `localStorage`**, which looks wrong for half a second and is not.
 *   It is the double-submit partner of a cookie this code cannot read; after a page reload the
 *   console has to prove same-origin possession of *something* to refresh, and the cookie is
 *   not it. `localStorage` is origin-scoped, so a cross-site attacker who can make the browser
 *   send the cookie still cannot read this value — which is exactly the property double-submit
 *   needs. (A readable cookie, the textbook alternative, cannot work here: the API is on a
 *   different origin from the console, so JavaScript here could not read a cookie it set.)
 *   Losing it is harmless — the user logs in again.
 *
 * - A **session kept in one tab** is the exception (ADR-040 in `platform`): a sign-in link opened
 *   in a browser that is already signed in. Its refresh token comes back in the body and lives in
 *   this tab's `sessionStorage`, and it refreshes and exits through its own routes, so the
 *   browser's cookie and its CSRF value — the admin's own session, in every other tab — are never
 *   read, replaced or cleared from here. A tab that has held one stays in that mode until it
 *   reloads without it.
 *
 * A 401 triggers one refresh and one retry. Concurrent 401s share a single in-flight refresh,
 * so a screen that fires six queries at once does not start six rotations — with rotation +
 * replay detection that would revoke the family and log the user out for using the app.
 */

/**
 * Where core-api lives. Read through a guard because `import.meta.env` is a Vite construct and
 * is undefined under plain Node — without this the module cannot be imported by the test
 * runner at all, which would put every helper in this file out of reach of a unit test.
 */
import { drainSseBuffer, sseData } from "./sse.js";
import { totalFromHeader } from "./paging.js";

function apiBase(): string {
  const fromEnv =
    typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_URL : undefined;
  return (fromEnv ?? "http://localhost:8080").replace(/\/$/, "");
}

const BASE = apiBase();
const CSRF_STORAGE_KEY = "acl.csrf";
const TAB_SESSION_KEY = "acl.tab-session";

/** A path on core-api as an address another system can use: a trigger's URL, which a zap or a CRM
 *  calls with no session. On AWS that is the console's own origin, under `/api`. */
export function apiUrl(path: string): string {
  return `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

let accessToken: string | null = null;
/** This tab's session is a sign-in link's, kept here and not on the cookie. */
let tabSession = readTabRefresh() !== null;
let refreshInFlight: Promise<boolean> | null = null;
let sessionController = new AbortController();

/** The lifetime of the current identity, not of its rotating access token. */
export function getSessionSignal(): AbortSignal {
  return sessionController.signal;
}

/** Retire identity-scoped work without discarding valid credentials or ending auth. */
export function invalidateSessionRequests(): void {
  const previous = sessionController;
  sessionController = new AbortController();
  refreshInFlight = null;
  previous.abort();
}

function assertActive(signal: AbortSignal): void {
  if (signal.aborted) throw new DOMException("Session operation cancelled", "AbortError");
}

/** Check both sides of every await: mocks, buffered bodies and transports may ignore abort. */
async function checked<T>(signal: AbortSignal, work: () => Promise<T>): Promise<T> {
  assertActive(signal);
  try {
    const result = await work();
    assertActive(signal);
    return result;
  } catch (error) {
    assertActive(signal);
    throw error;
  }
}

/** Notified when the session ends for any reason, so the app can route to /login. */
type SessionListener = () => void;
const sessionEndedListeners = new Set<SessionListener>();

export function onSessionEnded(listener: SessionListener): () => void {
  sessionEndedListeners.add(listener);
  return () => sessionEndedListeners.delete(listener);
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  csrf_token: string;
  /** Only for a sign-in link's session kept in one tab. */
  refresh_token?: string | null;
}

function readCsrf(): string | null {
  try {
    return localStorage.getItem(CSRF_STORAGE_KEY);
  } catch {
    return null; // Safari private mode and similar. Refresh then fails and we ask for a login.
  }
}

function writeCsrf(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(CSRF_STORAGE_KEY);
    else localStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch {
    /* non-fatal — the session simply will not survive a reload */
  }
}

function readTabRefresh(): string | null {
  try {
    return sessionStorage.getItem(TAB_SESSION_KEY);
  } catch {
    return null;
  }
}

function writeTabRefresh(token: string | null): void {
  try {
    if (token === null) sessionStorage.removeItem(TAB_SESSION_KEY);
    else sessionStorage.setItem(TAB_SESSION_KEY, token);
  } catch {
    /* non-fatal — the tab's session simply will not survive a reload */
  }
}

export function setSession(tokens: TokenResponse): void {
  accessToken = tokens.access_token;
  if (tokens.refresh_token) {
    tabSession = true;
    writeTabRefresh(tokens.refresh_token);
  } else {
    // A sign-in of this tab's own (the login page, after a tab session ended) is the browser's.
    tabSession = false;
    writeTabRefresh(null);
    writeCsrf(tokens.csrf_token);
  }
}

export function clearSession(): void {
  invalidateSessionRequests();
  accessToken = null;
  if (tabSession) writeTabRefresh(null);
  else writeCsrf(null);
  sessionEndedListeners.forEach((fn) => fn());
}

export function hasStoredSession(): boolean {
  return tabSession ? readTabRefresh() !== null : readCsrf() !== null;
}

/** Whether this tab holds a sign-in link's session of its own (ADR-040), or is starting one. */
export function isTabSession(): boolean {
  return tabSession;
}

/** For tests, which open many tabs in one module: back to the browser's session. */
export function forgetTabSession(): void {
  tabSession = false;
  writeTabRefresh(null);
}

async function parseError(res: Response): Promise<ApiError> {
  let detail: unknown;
  try {
    detail = await res.json();
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") throw error;
    detail = undefined;
  }
  const message = extractDetail(detail) ?? `${res.status} ${res.statusText}`;
  return new ApiError(res.status, message, detail);
}

/** FastAPI's `detail` is a string for our own errors and a list of issues for a 422. A route that
 *  says more than a sentence sends an object whose `message` is the sentence (a workflow's 422
 *  names the step with it); the rest stays on `ApiError.body` for the page that wants it. */
function extractDetail(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && typeof (detail as { message?: unknown }).message === "string") {
    return (detail as { message: string }).message;
  }
  if (Array.isArray(detail)) {
    const first = detail[0] as { msg?: string; loc?: unknown[] } | undefined;
    if (first?.msg) {
      const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : null;
      return field ? `${String(field)}: ${first.msg}` : first.msg;
    }
  }
  return null;
}

/**
 * Exchange the refresh cookie for a new access token. Returns whether a session survived.
 * Callers never await this directly — `request` does, and shares the promise.
 */
export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const signal = getSessionSignal();
  const stored = tabSession ? readTabRefresh() : readCsrf();
  if (!stored) {
    if (accessToken) clearSession();
    return false;
  }
  // A tab's session rotates its own token, and sends no cookie: the browser's is somebody else's.
  const send = tabSession
    ? () => fetch(`${BASE}/auth/sign-in-link/refresh`, {
        signal,
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: stored }),
      })
    : () => fetch(`${BASE}/auth/refresh`, {
        signal,
        method: "POST",
        credentials: "include",
        headers: { "X-CSRF-Token": stored },
      });

  const refreshing = (async () => {
    try {
      const res = await checked(signal, send);
      assertActive(signal);
      if (!res.ok) {
        clearSession();
        return false;
      }
      const tokens = await checked(signal, () => res.json() as Promise<TokenResponse>);
      assertActive(signal);
      setSession(tokens);
      return true;
    } catch (error) {
      assertActive(signal);
      if (error instanceof Error && error.name === "AbortError") throw error;
      // Network failure, not an auth failure: keep the stored CSRF so a later attempt can work
      // rather than logging the user out because their wifi dropped for a second.
      return false;
    }
  })();

  refreshInFlight = refreshing;
  try {
    return await refreshing;
  } finally {
    // A retired refresh may settle after the next identity has started its own rotation.
    if (refreshInFlight === refreshing) refreshInFlight = null;
  }
}

/** All authenticated transports have the same single-refresh, terminal-401 policy. */
async function retryUnauthorized(res: Response, signal: AbortSignal, retried = false): Promise<boolean> {
  assertActive(signal);
  if (res.status !== 401) return false;
  if (!retried && await checked(signal, () => refreshSession())) {
    assertActive(signal);
    return true;
  }
  assertActive(signal);
  // Only a page that has a session ends one. With no access token and nothing stored to refresh
  // with there is no session to end — and ending it anyway retired the anonymous query client
  // (`onSessionEnded` → `AuthProvider`), the one the dev phone gallery seeds its scenes into. The
  // refusal is still the caller's to hear.
  if (accessToken !== null || hasStoredSession()) clearSession();
  throw new ApiError(401, "Your session has expired. Please sign in again.");
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  /** Sent as JSON — or, for a `FormData`, as the multipart body it already is. */
  body?: unknown;
  /** Internal: prevents a refresh loop when the retried request also 401s. */
  _retried?: boolean;
}

async function requestWithResponse<T>(
  path: string,
  options: RequestOptions = {},
  sessionSignal = getSessionSignal(),
): Promise<{ data: T; response: Response }> {
  const { body, _retried, headers, ...rest } = options;
  const signal = options.signal
    ? AbortSignal.any([sessionSignal, options.signal])
    : sessionSignal;
  // A form goes as itself, with no Content-Type of ours: the browser writes the multipart
  // boundary into that header, and a hand-set one without it is a body the server cannot split.
  const form = typeof FormData !== "undefined" && body instanceof FormData;

  const res = await checked(signal, () => fetch(`${BASE}${path}`, {
    ...rest,
    signal,
    credentials: "include",
    headers: {
      ...(body !== undefined && !form ? { "Content-Type": "application/json" } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(headers as Record<string, string> | undefined),
    },
    body: form ? (body as FormData) : body !== undefined ? JSON.stringify(body) : undefined,
  }));

  if (await retryUnauthorized(res, signal, _retried)) {
    return requestWithResponse<T>(path, { ...options, _retried: true }, sessionSignal);
  }
  assertActive(signal);

  // 403 from a disabled account is terminal — refreshing would loop, so end the session here
  // rather than leaving the user staring at a permission error they cannot act on.
  if (res.status === 403 && !accessToken) {
    clearSession();
  }

  if (!res.ok) throw await checked(signal, () => parseError(res));
  if (res.status === 204) return { data: undefined as T, response: res };
  const data = await checked(signal, () => res.json() as Promise<T>);
  assertActive(signal);
  return { data, response: res };
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const signal = getSessionSignal();
  const { data } = await requestWithResponse<T>(path, options, signal);
  assertActive(signal);
  return data;
}

/**
 * One page of a list, plus how many rows the page is a page *of*.
 *
 * `total` is `null` when the server did not say — which is not the same as zero, and the two
 * must not render alike: "1-25 of 240" and "25 results" are honest, "25 of 0" is not. It goes
 * null in exactly one situation worth remembering: the API is on another origin, so
 * `X-Total-Count` is only readable if core-api also lists it in the CORS `expose_headers`.
 * The header is visible in devtools either way, so a missing total looks like a client bug
 * and is a server one.
 */
export interface Page<T> {
  items: T[];
  total: number | null;
}

/** What accepting an invitation sends (ADR-028 in `platform`). */
export interface InvitationAccept {
  token: string;
  password: string;
  /** Their name as they want it; null keeps the one the admin gave. */
  display_name?: string | null;
}

/**
 * POST a body that starts a session, and adopt the tokens it answers with. Clears the previous
 * session before its first await, which is what lets `AuthProvider` read the new identity's signal
 * straight after calling it.
 */
async function establishSession(path: string, body: unknown, tab = false): Promise<TokenResponse> {
  // Into tab mode before the clear, so clearing touches only this tab and never the browser's CSRF.
  if (tab) tabSession = true;
  clearSession();
  const signal = getSessionSignal();
  const res = await checked(signal, () => fetch(`${BASE}${path}`, {
    signal,
    method: "POST",
    credentials: tab ? "omit" : "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }));
  if (!res.ok) throw await checked(signal, () => parseError(res));
  const tokens = await checked(signal, () => res.json() as Promise<TokenResponse>);
  assertActive(signal);
  setSession(tokens);
  return tokens;
}

export const api = {
  get: <T>(path: string) => request<T>(path),

  /** A list route that paginates. Same request path as `get`; also reads `X-Total-Count`. */
  async getPage<T>(path: string): Promise<Page<T>> {
    const signal = getSessionSignal();
    const { data, response } = await requestWithResponse<T[]>(path, {}, signal);
    assertActive(signal);
    return { items: data ?? [], total: totalFromHeader(response.headers.get("X-Total-Count")) };
  },

  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  /** A multipart POST: a file and the form fields that describe it. */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),

  /** Login is special: it establishes the session rather than using one. */
  login: (email: string, password: string) => establishSession("/auth/login", { email, password }),

  /** Accepting an invitation makes the account and signs it in, the way a login does. */
  acceptInvitation: (input: InvitationAccept) => establishSession("/auth/invitation/accept", input),

  /** An admin's sign-in link (ADR-040 in `platform`) signs this browser in as its person, or with
   *  `tab`, only this tab, leaving the browser's own session as it is. */
  signInWithLink: (token: string, tab = false) =>
    tab
      ? establishSession("/auth/sign-in-link", { token, tab: true }, true)
      : establishSession("/auth/sign-in-link", { token }),

  /**
   * What an invitation's link offers. No session, and deliberately outside the session's signal:
   * the token is the credential, and the sign-in that accepting starts must not cancel the read
   * the page is showing.
   */
  async previewInvitation<T>(token: string, signal?: AbortSignal): Promise<T> {
    const res = await fetch(`${BASE}/auth/invitation`, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) throw await parseError(res);
    return (await res.json()) as T;
  },

  /** Choosing a new password from a reset link signs the browser in, the way accepting an
   *  invitation does (ADR-057 in `platform`). */
  completePasswordReset: (token: string, password: string) =>
    establishSession("/auth/password-reset/complete", { token, password }),

  /**
   * The three reset calls that take no session. Outside the session's signal, like
   * `previewInvitation`: the token is the credential, and the sign-in that completing starts must
   * not cancel the read the page is showing.
   */
  async publicPost<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const res = await fetch(`${BASE}${path}`, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw await parseError(res);
    return (await res.json()) as T;
  },

  async publicGet<T>(path: string, signal?: AbortSignal): Promise<T> {
    const res = await fetch(`${BASE}${path}`, { signal });
    if (!res.ok) throw await parseError(res);
    return (await res.json()) as T;
  },

  async logout(): Promise<void> {
    // A tab's session exits by its own token; the cookie's logout would end the browser's session.
    const tabToken = tabSession ? readTabRefresh() : null;
    const tab = tabSession;
    // Leaving the console leaves the phones (ADR-052 in `platform`). Sent BEFORE the session is
    // cleared, since it needs this session's token, and not for a tab session: an admin signed in
    // as somebody has no phone here and core-api refuses agent actions from one.
    const lines = tab ? null : signOutOfLines();
    // Clear before the network await. An old logout must never clear a later login.
    clearSession();
    const signal = getSessionSignal();
    // Still attempt the server logout. Aborting cannot undo server-side work or a Set-Cookie
    // already processed by the browser; cross-request cookie ordering needs server support.
    if (tab) {
      if (tabToken) {
        await checked(signal, () => fetch(`${BASE}/auth/sign-in-link/exit`, {
          method: "POST",
          credentials: "omit",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: tabToken }),
          signal,
        }));
      }
    } else {
      await checked(signal, () => fetch(`${BASE}/auth/logout`, {
        method: "POST", credentials: "include", signal,
      }));
    }
    if (lines) await lines;
    assertActive(signal);
  },
};

/**
 * Sign out of the ACD's lines, best effort. Without it a rep who signs out of the console stays on
 * the live floor as "Signed in, phone offline" until their suspension times out (ADR-052 in
 * `platform`) — the web session and the ACD session are two different things, and the rep meant to
 * end both.
 *
 * Deliberately not `request()`: this goes out as the session is ending, so it reads the token
 * itself rather than racing `clearSession`, and it stays outside the session signal that logging
 * out aborts. An account with no phone answers 409 and every failure is swallowed — nothing here
 * is worth keeping somebody signed in for.
 */
function signOutOfLines(): Promise<void> {
  const token = accessToken;
  return fetch(`${BASE}/acd/agent/logout`, {
    method: "POST",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }).then(
    () => undefined,
    () => undefined,
  );
}

export interface StreamHandlers {
  /** One SSE data payload (the text after `data:`), in arrival order. */
  onData: (data: string) => void;
  /** The stream ended for any reason other than `close()` — server gone, network dropped,
   *  session unrecoverable. SSE has no clean end in this app: every stream is "until closed". */
  onEnd?: (error: unknown) => void;
}

/**
 * An authenticated SSE subscription. `EventSource` cannot carry the bearer header (and this
 * module keeps the token where only a header can reach it), so the stream is a plain `fetch`
 * read incrementally. One 401 gets one refresh and one reconnect, same as `request`; there is
 * no other retry — the caller decides whether a dropped stream is worth surfacing, because
 * only it knows whether the thing being streamed still exists.
 */
export function streamEvents(path: string, handlers: StreamHandlers): { close: () => void } {
  const controller = new AbortController();
  const signal = AbortSignal.any([controller.signal, getSessionSignal()]);

  const run = async (): Promise<void> => {
    let retried = false;
    for (;;) {
      const res = await checked(signal, () => fetch(`${BASE}${path}`, {
        credentials: "include",
        headers: {
          Accept: "text/event-stream",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        signal,
      }));
      if (await retryUnauthorized(res, signal, retried)) {
        retried = true;
        continue;
      }
      if (!res.ok || !res.body) throw await checked(signal, () => parseError(res));

      const reader = res.body.getReader();
      const cancelReader = () => { void reader.cancel().catch(() => {}); };
      signal.addEventListener("abort", cancelReader, { once: true });
      try {
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { done, value } = await checked(signal, () => reader.read());
          if (done) return;
          buffer += decoder.decode(value, { stream: true });
          const drained = drainSseBuffer(buffer);
          buffer = drained.rest;
          for (const frame of drained.frames) {
            assertActive(signal); // onData may synchronously close or change identity.
            const data = sseData(frame);
            if (data !== null) handlers.onData(data);
          }
        }
      } finally {
        signal.removeEventListener("abort", cancelReader);
        cancelReader();
        reader.releaseLock();
      }
    }
  };

  void run()
    .then(() => {
      if (!signal.aborted) handlers.onEnd?.(undefined);
    })
    .catch((error: unknown) => {
      // Closing or retiring a session is not an ending the old subscriber should act on.
      if (!signal.aborted) handlers.onEnd?.(error);
    });

  return { close: () => controller.abort() };
}

/**
 * A file the API generates on demand — an export, not a static asset.
 *
 * Cannot be an `<a download href>`: core-api is on another origin and authorises with a bearer
 * header this module keeps out of reach of the DOM, so a plain link arrives unauthenticated and
 * downloads a 401 body as a `.csv`. So the file is fetched like any other request and handed
 * back as a blob for the caller to save.
 *
 * One 401 gets one refresh and one retry, the same as `request`. `headers` comes back with the
 * blob because a generated file sometimes cannot describe itself — the calls export says
 * whether it hit its row cap in `X-Export-Truncated`, and a truncated CSV is indistinguishable
 * from a complete one once it is open in a spreadsheet. Only headers core-api lists in the CORS
 * `expose_headers` are readable here.
 */
export async function fetchFile(
  path: string,
  _retried = false,
  signal = getSessionSignal(),
): Promise<{ blob: Blob; headers: Headers }> {
  const res = await checked(signal, () => fetch(`${BASE}${path}`, {
    signal,
    credentials: "include",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  }));

  if (await retryUnauthorized(res, signal, _retried)) {
    const file = await fetchFile(path, true, signal);
    assertActive(signal);
    return file;
  }
  if (!res.ok) throw await checked(signal, () => parseError(res));

  const blob = await checked(signal, () => res.blob());
  assertActive(signal);
  return { blob, headers: res.headers };
}

/**
 * A file the API makes from what it is sent: `fetchFile`, as a POST with a JSON body. The voice
 * settings' test is one: a sentence goes up and its sound comes back (ADR-114 in `platform`).
 *
 * One 401 gets one refresh and one retry, and a refusal is an `ApiError` with core-api's sentence,
 * the same as everywhere else.
 */
export async function postForFile(
  path: string,
  body: unknown,
  _retried = false,
  signal = getSessionSignal(),
): Promise<{ blob: Blob; headers: Headers }> {
  const res = await checked(signal, () => fetch(`${BASE}${path}`, {
    method: "POST",
    signal,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(body),
  }));

  if (await retryUnauthorized(res, signal, _retried)) {
    const file = await postForFile(path, body, true, signal);
    assertActive(signal);
    return file;
  }
  if (!res.ok) throw await checked(signal, () => parseError(res));

  const blob = await checked(signal, () => res.blob());
  assertActive(signal);
  return { blob, headers: res.headers };
}

/**
 * Save a blob to the user's disk under `filename`.
 *
 * The object URL is revoked on the next tick rather than immediately: revoking it in the same
 * task as the synthetic click races the browser's own read of it, and the failure is a download
 * that silently never starts in some browsers and works in others.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export type QueryValue = string | number | boolean | null | undefined;

/** Build a query string from a filter object, dropping empty values. */
export function qs(params: Readonly<Record<string, QueryValue>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : "";
}
